import React from 'react';
import s from './s.module.less';
import providers from '../../imgs/providers.png';
import insurance from '../../imgs/insurance.png';
import insuranceM from '../../imgs/insurance-m.png';
import providersM from '../../imgs/providers-m.png';
import { TLeadFormStepData } from 'api/ppaLeadForm';
import useSettings from 'hooks/useSettings';
import { EnumFields } from 'types/enumerationData';
import { sortByProperty } from 'utils/form';
import { isCredentialingHelpType, hasIntent, EHelpType } from '../../../PPALeadFormPage/helpType';

interface CelebrationSectionProps {
    leadFormData?: TLeadFormStepData;
}

const CheckIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
        <path d="M5 13l4 4L19 7" />
    </svg>
);

// Credentialing 路径
function CredentialingHero() {
    return (
        <div className={s.contentContainer}>
            <div className={s.titleSection}>
                <h1 className={s.title}>
                    <span className={s.regularText}>Get credentialed with </span>
                    <span className={s.blueText}>major insurance payers</span>
                </h1>
            </div>

            <div className={s.featuresSection}>
                <div className={s.featureItem}>
                    <div className={s.checkIcon}>
                        <CheckIcon />
                    </div>
                    <p className={s.featureText}>
                        <strong>Stay independent.</strong> Your payer contracts stay with your practice, not with us.
                    </p>
                </div>

                <div className={s.featureItem}>
                    <div className={s.checkIcon}>
                        <CheckIcon />
                    </div>
                    <p className={s.featureText}>
                        <strong>Major insurance payers.</strong> We manage your applications from start to finish, so you can expand your coverage without running the process yourself.
                    </p>
                </div>

                <div className={s.featureItem}>
                    <div className={s.checkIcon}>
                        <CheckIcon />
                    </div>
                    <p className={s.featureText}>
                        <strong>A small, refundable fee per panel.</strong> Cash back can make your first three panels essentially free.
                    </p>
                </div>

                <div className={s.insuranceLogos}>
                    <img src={insurance} alt="insurance" className={s.pc} />
                    <img src={insuranceM} alt="insurance" className={s.mobile} />
                </div>
            </div>
        </div>
    );
}

// Insurance billing 路径
function BillingHero() {
    return (
        <div className={s.contentContainer}>
            <div className={s.titleSection}>
                <h1 className={s.title}>
                    <span className={s.regularText}>Get paid faster with </span>
                    <span className={s.blueText}>Managed billing</span>
                </h1>
            </div>

            <div className={s.featuresSection}>
                <div className={s.featureItem}>
                    <div className={s.checkIcon}>
                        <CheckIcon />
                    </div>
                    <p className={s.featureText}>
                        <strong>3.9% of reimbursement</strong>, about half the industry average. We only get paid when you get paid.
                    </p>
                </div>

                <div className={s.featureItem}>
                    <div className={s.checkIcon}>
                        <CheckIcon />
                    </div>
                    <p className={s.featureText}>
                        <strong>No biller needed.</strong> Claims go out within 24 hours of every session, around the clock.
                    </p>
                </div>

                <div className={s.featureItem}>
                    <div className={s.checkIcon}>
                        <CheckIcon />
                    </div>
                    <p className={s.featureText}>
                        <strong>94% first-pass claim rate</strong> against a 70–80% industry average, and every denial appealed.
                    </p>
                </div>

                <div className={s.insuranceLogos}>
                    <img src={insurance} alt="insurance" className={s.pc} />
                    <img src={insuranceM} alt="insurance" className={s.mobile} />
                </div>
            </div>
        </div>
    );
}

// EHR 路径
function EhrHero() {
    return (
        <div className={s.contentContainer}>
            <div className={s.titleSection}>
                <h1 className={s.title}>
                    <span className={s.regularText}>A </span>
                    <span className={s.blueText}>free EHR</span>
                    <span className={s.regularText}> that runs your entire practice</span>
                </h1>
            </div>

            <div className={s.featuresSection}>
                <div className={s.featureItem}>
                    <div className={s.checkIcon}>
                        <CheckIcon />
                    </div>
                    <p className={s.featureText}>
                        <strong>Patient care.</strong> Telehealth, messaging, text visits and e-prescribing, so booking and reaching you is easy for your patients.
                    </p>
                </div>

                <div className={s.featureItem}>
                    <div className={s.checkIcon}>
                        <CheckIcon />
                    </div>
                    <p className={s.featureText}>
                        <strong>Operations.</strong> Scheduling, documentation, charts, intake flows and eFax in one HIPAA-compliant place.
                    </p>
                </div>

                <div className={s.featureItem}>
                    <div className={s.checkIcon}>
                        <CheckIcon />
                    </div>
                    <p className={s.featureText}>
                        <strong>No cost, no contract.</strong> The whole platform is free, with nothing to migrate off later.
                    </p>
                </div>
            </div>
        </div>
    );
}

export default function CelebrationSection({ leadFormData }: CelebrationSectionProps) {
    const [setting] = useSettings();

    const stateOptions = sortByProperty(setting[EnumFields.STATE] || [], 'label').map((option) => ({
        id: option.value as string,
        label: option.label,
    }));
    const getStatesName = (states?: string) => {
        if (!states) {
            return '';
        }
        const statesName = stateOptions.find((item) => item.id === states.split(',')?.[0])?.label;
        return statesName;
    };

    const getServicesName = (services?: string[]) => {
        if (!services) {
            return '';
        }
        return services[0];
    };

    const helpType = leadFormData?.practiceHelpType;

    // 按优先级：Credentialing > Insurance billing > Patient growth > EHR
    if (isCredentialingHelpType(helpType)) {
        return <CredentialingHero />;
    }

    if (hasIntent(helpType, EHelpType.INSURANCE_BILLING)) {
        return <BillingHero />;
    }

    if (hasIntent(helpType, EHelpType.EHR) && !hasIntent(helpType, EHelpType.PATIENT_GROWTH)) {
        return <EhrHero />;
    }

    // Default: Patient growth 或 fallback
    return (
        <div className={s.contentContainer}>
            <div className={s.titleSection}>
                <h1 className={s.title}>
                    <span className={s.regularText}>Providers on Klarity practice see </span>
                    <span className={s.blueText}>30+ bookings</span>
                    <span className={s.regularText}>{' in the '}</span>
                    <span className={s.blueText}>first month</span>
                </h1>
            </div>

            <div className={s.featuresSection}>
                <div className={s.featureItem}>
                    <div className={s.checkIcon}>
                        <CheckIcon />
                    </div>
                    <p className={s.featureText}>
                        <span className={s.blueText}>No-show support</span>
                        <span className={s.regularText}>
                            {' '}
                            — tools to help reduce missed appointments and protect your time
                        </span>
                    </p>
                </div>

                <div className={s.featureItem}>
                    <div className={s.checkIcon}>
                        <CheckIcon />
                    </div>
                    <p className={s.featureText}>
                        <span className={s.blueText}>Easily manage</span>
                        <span className={s.regularText}> patient growth velocity</span>
                    </p>
                </div>

                <div className={s.featureItem}>
                    <div className={s.checkIcon}>
                        <CheckIcon />
                    </div>
                    <p className={s.featureText}>
                        <span className={s.regularText}>Accepts </span>
                        <span className={s.blueText}>major insurance</span>
                        <span className={s.regularText}> to maximize your case load</span>
                    </p>
                </div>

                <div className={s.insuranceLogos}>
                    <img src={insurance} alt="insurance" className={s.pc} />
                    <img src={insuranceM} alt="insurance" className={s.mobile} />
                </div>
            </div>

            <div className={s.providersSection}>
                <img src={providers} alt="providers" className={s.pc} />
                <img src={providersM} alt="providers" className={s.mobile} />
            </div>
        </div>
    );
}
